import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./UserDashboard.scss";

interface User {
  id: number;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
}

interface PaginatedResponse {
  content: User[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
}

const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
};

const UserDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [page, setPage] = useState<number>(0);
  const [size, setSize] = useState<number>(10);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [totalElements, setTotalElements] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams({
          searchTerm: debouncedSearchTerm,
          page: page.toString(),
          size: size.toString(),
        });

        const { data } = await axios.get(`/users?${params.toString()}`);

        if (data) {
          setUsers(data.content);
          setTotalPages(data.totalPages);
          setTotalElements(data.totalElements);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setError("Failed to load data. Please try again later.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [debouncedSearchTerm, page, size]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newSize: number) => {
    setSize(newSize);
    setPage(0);
  };

  const handleRowClick = (userId: number) => {
    navigate(`/users/${userId}`);
  };

  const handleCreateNew = () => {
    navigate("/users/new");
  };

  return (
    <div className="user-dashboard-container">
      <header className="user-dashboard-header">
        <div className="user-header-content">
          <h1 className="user-dashboard-title">User Management Dashboard</h1>
          <div className="user-header-actions">
            <div className="user-search-filters">
              <input
                type="text"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <button className="user-create-button" onClick={handleCreateNew}>
              Create New User
            </button>
          </div>
        </div>
      </header>

      <main className="user-dashboard-content">
        {isLoading ? (
          <div className="user-loading">Loading data...</div>
        ) : error ? (
          <div className="user-error-state">{error}</div>
        ) : users.length === 0 ? (
          <div className="user-empty-state">No users found</div>
        ) : (
          <div className="user-table-container">
            <table className="user-table">
              <thead>
                <tr>
                  <th>Username</th>
                  <th>Email</th>
                  <th>First Name</th>
                  <th>Last Name</th>
                  <th>Phone Number</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr
                    key={user.id}
                    onClick={() => handleRowClick(user.id)}
                    className="user-clickable-row"
                  >
                    <td>{user.username}</td>
                    <td>{user.email}</td>
                    <td>{user.firstName}</td>
                    <td>{user.lastName}</td>
                    <td>{user.phoneNumber}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      <footer className="user-dashboard-footer">
        <div className="user-pagination-controls">
          <div className="user-page-size-control">
            <label>Rows per page:</label>
            <select
              value={size}
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
          </div>

          <div className="user-pagination">
            <button
              className="user-pagination-button"
              onClick={() => handlePageChange(0)}
              disabled={page === 0}
            >
              First
            </button>
            <button
              className="user-pagination-button"
              onClick={() => handlePageChange(page - 1)}
              disabled={page === 0}
            >
              Previous
            </button>
            <span className="user-page-info">
              Page {page + 1} of {totalPages}
            </span>
            <button
              className="user-pagination-button"
              onClick={() => handlePageChange(page + 1)}
              disabled={page >= totalPages - 1}
            >
              Next
            </button>
            <button
              className="user-pagination-button"
              onClick={() => handlePageChange(totalPages - 1)}
              disabled={page >= totalPages - 1}
            >
              Last
            </button>
          </div>

          <div className="user-total-items">Total Items: {totalElements}</div>
        </div>
      </footer>
    </div>
  );
};

export default UserDashboard;
